/*
 * [2026-10-14] Collection card swatches swap the card in place.
 *
 * Clicking a color swatch on a product card used to open that colorway's PDP.
 * Now it swaps the card itself (image + hover image, title, price, sale promo,
 * labels, quick shop and links) to the clicked colorway, without leaving the
 * collection page. Each colorway's card is rendered by the theme's own
 * product-block snippet via templates/collection.card-swap-raw.liquid, fetched
 * once per group tag per page (prefetched when a shopper hovers/touches the
 * swatch row). Cmd/Ctrl/Shift/middle-click still open the PDP, and if the
 * fetch fails the swatch falls back to opening the PDP.
 */
(() => {
  if (window.__cardSwatchSwap) return;
  window.__cardSwatchSwap = true;

  const style = document.createElement('style');
  style.dataset.cardSwatchSwap = '';
  style.textContent = `
    product-block.card-swap--loading .image-cont { opacity: .55; transition: opacity .15s ease; }
    card-linked-swatches .card-swatch { cursor: pointer; }
  `;
  document.head.appendChild(style);

  const cache = new Map(); // group tag -> Promise<Map<handle, product-block element>>

  const handleFromUrl = (href) => {
    try {
      const m = new URL(href, window.location.origin).pathname.match(/\/products\/([^/?#]+)/);
      return m ? decodeURIComponent(m[1]) : null;
    } catch (e) {
      return null;
    }
  };

  const loadCards = (group) => {
    if (!cache.has(group)) {
      const p = fetch(`/collections/all/${encodeURIComponent(group)}?view=card-swap-raw`)
        .then((res) => (res.ok ? res.text() : Promise.reject(new Error(`HTTP ${res.status}`))))
        .then((html) => {
          const tpl = document.createElement('template');
          tpl.innerHTML = html;
          const map = new Map();
          tpl.content.querySelectorAll('[data-card-swap]').forEach((wrap) => {
            const block = wrap.querySelector('product-block');
            if (block) map.set(wrap.dataset.cardSwap, block);
          });
          return map;
        });
      p.catch(() => cache.delete(group)); // allow a retry on the next interaction
      cache.set(group, p);
    }
    return cache.get(group);
  };

  // Warm the cache (and the hovered colorway's main image) before the click.
  const preloaded = new Set();
  const prefetch = (evt) => {
    const row = evt.target && evt.target.closest ? evt.target.closest('card-linked-swatches[data-group]') : null;
    if (!row) return;
    const swatch = evt.target.closest('.card-swatch');
    loadCards(row.dataset.group)
      .then((cards) => {
        const handle = swatch && handleFromUrl(swatch.href);
        if (!handle || preloaded.has(handle)) return;
        const img = cards.get(handle)?.querySelector('.product-block__image--active img');
        if (!img) return;
        preloaded.add(handle);
        const pre = new Image();
        const srcset = img.getAttribute('srcset');
        if (srcset) pre.srcset = srcset;
        pre.sizes = img.getAttribute('sizes') || '';
        pre.src = img.getAttribute('src');
      })
      .catch(() => {});
  };
  ['pointerover', 'focusin', 'touchstart'].forEach((type) =>
    document.addEventListener(type, prefetch, { passive: true })
  );

  const swapCard = (oldBlock, source, handle, row, clicked) => {
    const newBlock = document.importNode(source, true);
    newBlock.querySelectorAll('style').forEach((el) => el.remove()); // already on the page

    // Keep classes the page added to this card (e.g. slider__item in carousels).
    oldBlock.classList.forEach((cl) => {
      if (cl !== 'expanded' && !cl.startsWith('card-swap--')) newBlock.classList.add(cl);
    });

    // Match this grid's image shape and sizes (the grid picks them per collection).
    const oldAr = oldBlock.querySelector('.img-ar');
    const ar = oldAr && oldAr.style.getPropertyValue('--aspect-ratio');
    if (ar) newBlock.querySelectorAll('.image-cont .img-ar').forEach((el) => el.style.setProperty('--aspect-ratio', ar));
    const oldImg = oldBlock.querySelector('.image-cont img[sizes]');
    if (oldImg) newBlock.querySelectorAll('.image-cont img[sizes]').forEach((el) => el.setAttribute('sizes', oldImg.getAttribute('sizes')));

    // Keep collection-scoped product URLs (/collections/x/products/y) if the grid uses them.
    const oldLink = oldBlock.querySelector('a.product-link[href]');
    const scoped = oldLink && new URL(oldLink.href, window.location.origin).pathname.match(/^(.*\/collections\/[^/]+)\/products\//);
    if (scoped) {
      newBlock.querySelectorAll('a.product-link[href], a.quickbuy-toggle[href]').forEach((a) => {
        const u = new URL(a.getAttribute('href'), window.location.origin);
        u.pathname = `${scoped[1]}/products/${handle}`;
        a.setAttribute('href', u.pathname + u.search);
      });
    }

    // Reuse the already-rendered swatch row so it doesn't flicker, then move the highlight.
    const newRow = newBlock.querySelector('card-linked-swatches');
    if (newRow) newRow.replaceWith(row);
    row.dataset.handle = handle;
    row.querySelectorAll('.card-swatch').forEach((a) => {
      const active = handleFromUrl(a.href) === handle;
      a.classList.toggle('card-swatch--active', active);
      if (active) a.setAttribute('aria-current', 'true');
      else a.removeAttribute('aria-current');
    });

    const hadFocus = document.activeElement === clicked;
    oldBlock.replaceWith(newBlock);
    if (hadFocus) clicked.focus({ preventScroll: true });

    document.dispatchEvent(new CustomEvent('card:swatch-swap', {
      detail: { from: oldBlock.dataset.productId, to: newBlock.dataset.productId, handle, block: newBlock },
    }));
  };

  document.addEventListener('click', (evt) => {
    const swatch = evt.target && evt.target.closest ? evt.target.closest('card-linked-swatches .card-swatch[href]') : null;
    if (!swatch) return;
    if (evt.defaultPrevented || evt.button !== 0 || evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey) return;

    const row = swatch.closest('card-linked-swatches');
    const block = swatch.closest('product-block');
    if (!row || !block || !row.dataset.group || block.classList.contains('expanded')) return;

    evt.preventDefault();
    const handle = handleFromUrl(swatch.href);
    if (!handle || handle === row.dataset.handle) return; // already showing this color

    const token = String(Date.now() + Math.random());
    block.dataset.swapToken = token;
    block.classList.add('card-swap--loading');

    loadCards(row.dataset.group)
      .then((cards) => {
        if (!block.isConnected || block.dataset.swapToken !== token) return; // a newer click won
        const source = cards.get(handle);
        if (!source) throw new Error('Colorway card not found');
        swapCard(block, source, handle, row, swatch);
      })
      .catch(() => {
        block.classList.remove('card-swap--loading');
        window.location.href = swatch.href;
      });
  });
})();
